/* eslint-disable @typescript-eslint/ban-types */
import { SubscriptionOptions } from '@reduxjs/toolkit/dist/query/core/apiState';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import {
  TypedUseQueryHookResult,
  UseQueryStateOptions
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import {
  BaseKey,
  BaseRecord,
  GetOneParams,
  GetOneResponse,
  IBaseQueryFn,
  MetaQuery
} from 'app/context/data/types';
import { useResource } from '../resource';
import { useDataProvider } from './useDataProvider';
import { pickDataProvider } from 'app/definitions/helpers/pickDataProvider';

export type UseOneProps<TData> = {
  resource?: string;
  id?: BaseKey;
  queryOptions?: SubscriptionOptions &
    UseQueryStateOptions<
      QueryDefinition<
        GetOneParams,
        IBaseQueryFn,
        string,
        GetOneResponse<TData>,
        string
      >,
      GetOneResponse<TData>
    >;
  meta?: MetaQuery;
  dataProviderName?: string;
};

export const useOne = <
  TQueryFnData extends BaseRecord = BaseRecord,
  TData extends BaseRecord = TQueryFnData
>(
  props: UseOneProps<TData>
): TypedUseQueryHookResult<TData, GetOneParams, any> => {
  const {
    resource: resourceFromProp,
    id,
    queryOptions = {},
    meta,
    dataProviderName
  } = props;

  const { resources, resource, identifier } = useResource(resourceFromProp);

  const dataProvider = useDataProvider();

  const pickedDataProvider = pickDataProvider(
    identifier,
    dataProviderName,
    resources
  );
  const api = dataProvider(pickedDataProvider);
  const { useGetOneQuery } = api;
  const query = useGetOneQuery(
    {
      resource: resource.name ?? '',
      id,
      meta: meta
    },
    {
      ...queryOptions,
      skip: typeof queryOptions.skip === 'undefined' ? !id : queryOptions.skip
    }
  );
  return query as unknown as TypedUseQueryHookResult<TData, GetOneParams, any>;
};
